import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../../components/Loading';
import usePermissions from '../../../components/Menu/MenuItems/hooks/usePermissions';
import BankAccountStatusBadge from '../components/BankAccountStatusBadge';
import ConfirmDialog from '../components/ConfirmDialog';
import CredentialsSection from './components/CredentialsSection';
import {
   getBankAccount,
   setBankAccountStatus,
} from '../../../services/finance/bankAccountsApi';
import { listCredentials } from '../../../services/finance/integrationCredentialsApi';
import { readApiError, financeErrorMessage } from '../utils/apiError';
import {
   formatBankNumber,
   formatPixKey,
   formatDateTimeBr,
   orDash,
} from '../utils/format';
import { formatDocForDisplay } from '../../../utils/format';
import { ACCOUNT_TYPE_LABELS } from '../constants';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import {
   EntityActionBar,
   ActionBarSpacer,
} from '../../../styles/ui/ActionBar';
import {
   CtaButton,
   PrimaryButton,
   DangerButton,
   GhostButton,
} from '../../../styles/ui/Button';
import { TextLink } from '../../../styles/ui/Link';
import { ErrorState } from '../../../styles/feedback/ErrorState';

// ---------------------------------------------------------------------------
// Financeiro · Contas bancárias — ficha (handoff 0079).
//
// Concentra as duas metades do módulo: os dados cadastrais da conta e as
// CREDENCIAIS de integração (§3.2/§3.3), que só fazem sentido no contexto da
// conta a que pertencem.
//
// Não existe excluir: conta se DESATIVA (há credenciais e, em breve, cobranças
// apontando para ela). Por isso a barra de ações não tem ação destrutiva de
// exclusão — "Desativar" é escrita de estado, não destruição.
// ---------------------------------------------------------------------------

const DataGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 16px;

   @media (max-width: 768px) {
      grid-template-columns: 1fr;
   }
`;

const DataItem = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;

   span.label {
      font-size: 0.7rem;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      text-transform: uppercase;
   }
   span.value {
      font-size: 0.9rem;
      color: ${({ theme }) => theme?.colors?.text ?? '#111'};
      word-break: break-word;
   }
`;

const BackRow = styled.div`
   margin-bottom: 12px;
`;

export default function BankAccountShow() {
   const navigate = useNavigate();
   const location = useLocation();
   const { id } = useParams();
   const { hasPermission } = usePermissions();

   const canUpdate = hasPermission('finance:bank_account:update');
   const canReadCredentials = hasPermission('finance:credential:read');

   const [account, setAccount] = useState(null);
   const [credentials, setCredentials] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [statusDialogOpen, setStatusDialogOpen] = useState(false);
   const [isSavingStatus, setIsSavingStatus] = useState(false);

   // Conta e credenciais carregam juntas: as ações de credencial mudam o que a
   // ficha mostra (alerta de "não emite"), então recarregar as duas mantém a
   // tela coerente depois de qualquer mutação.
   const fetchAll = useCallback(async () => {
      setIsLoading(true);
      try {
         const loaded = await getBankAccount(id);
         if (!loaded) {
            toast.error('Conta bancária não encontrada.');
            navigate('/finance/bank-accounts', { replace: true });
            return;
         }
         setAccount(loaded);

         if (canReadCredentials) {
            const list = await listCredentials(id);
            setCredentials(list);
         }
      } catch (err) {
         toast.error(financeErrorMessage(err, 'Erro ao carregar a conta.'));
      } finally {
         setIsLoading(false);
      }
   }, [id, navigate, canReadCredentials]);

   useEffect(() => {
      fetchAll();
   }, [fetchAll]);

   // Voltar via histórico restaura a listagem com os filtros de onde o usuário
   // saiu (navegacao.md §1.6); deep-link direto cai no fallback.
   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/finance/bank-accounts');
   };

   const handleToggleStatus = async () => {
      if (isSavingStatus || !account) return;
      const nextStatus = account.status === 'active' ? 'inactive' : 'active';

      setIsSavingStatus(true);
      try {
         const { message } = await setBankAccountStatus(account.id, nextStatus);
         toast.success(message);
         setStatusDialogOpen(false);
         fetchAll();
      } catch (err) {
         const { code } = readApiError(err);
         // Mudar para a situação que já vigora é aviso, não erro grave: outra
         // aba já fez a troca. Fecha o diálogo e ressincroniza.
         if (code === 'bank_account_status_unchanged') {
            toast.info('A conta já está nessa situação.');
            setStatusDialogOpen(false);
            fetchAll();
         } else {
            toast.error(
               financeErrorMessage(err, 'Erro ao alterar a situação da conta.'),
            );
         }
      } finally {
         setIsSavingStatus(false);
      }
   };

   // Falha de carga não pode virar tela em branco: ou está carregando, ou o
   // usuário recebe um estado explícito com saída (FC4 — erro nunca silencioso).
   if (!account) {
      return (
         <Container>
            <Loading isLoading={isLoading} />
            {!isLoading && (
               <ErrorState
                  title="Não foi possível carregar a conta"
                  description="A conta pode ter sido removida ou houve uma falha de comunicação com o servidor."
                  action={
                     <>
                        <CtaButton type="button" onClick={fetchAll}>
                           Tentar novamente
                        </CtaButton>
                        <GhostButton
                           type="button"
                           onClick={() => navigate('/finance/bank-accounts')}
                        >
                           Voltar para a lista
                        </GhostButton>
                     </>
                  }
               />
            )}
         </Container>
      );
   }

   const isActive = account.status === 'active';

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <BackRow>
            <TextLink as="button" type="button" onClick={handleBack}>
               ← Voltar
            </TextLink>
         </BackRow>

         <PageHeader>
            <PageHeaderText>
               <PageTitle>
                  {orDash(account.account_name)}{' '}
                  <BankAccountStatusBadge value={account.status} />
               </PageTitle>
               <PageSubtitle>
                  {orDash(account.account_owner_name)} ·{' '}
                  {formatDocForDisplay(account.account_doc_number)}
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         {canUpdate && (
            <EntityActionBar>
               {isActive && (
                  <>
                     {/* Desativar fica isolada à esquerda: é a ação de maior
                         impacto da tela (a conta para de operar), mesmo não
                         sendo exclusão. */}
                     <DangerButton
                        type="button"
                        onClick={() => setStatusDialogOpen(true)}
                     >
                        Desativar conta
                     </DangerButton>
                     <ActionBarSpacer />
                  </>
               )}
               <CtaButton
                  type="button"
                  onClick={() =>
                     navigate(`/finance/bank-accounts/${account.id}/edit`)
                  }
               >
                  Editar
               </CtaButton>
               {!isActive && (
                  <PrimaryButton
                     type="button"
                     onClick={() => setStatusDialogOpen(true)}
                  >
                     Reativar conta
                  </PrimaryButton>
               )}
            </EntityActionBar>
         )}

         {/* ─── Dados cadastrais ──────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Dados da conta</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <DataGrid>
                  <DataItem>
                     <span className="label">Apelido</span>
                     <span className="value">{orDash(account.account_name)}</span>
                  </DataItem>
                  <DataItem>
                     <span className="label">Titular</span>
                     <span className="value">
                        {orDash(account.account_owner_name)}
                     </span>
                  </DataItem>
                  <DataItem>
                     <span className="label">CPF / CNPJ</span>
                     <span className="value">
                        {formatDocForDisplay(account.account_doc_number)}
                     </span>
                  </DataItem>

                  <DataItem>
                     <span className="label">Banco</span>
                     <span className="value">
                        {account.bank_name || account.bank_code
                           ? `${orDash(account.bank_name)}${
                                account.bank_code ? ` (${account.bank_code})` : ''
                             }`
                           : '—'}
                     </span>
                  </DataItem>
                  <DataItem>
                     <span className="label">Agência</span>
                     <span className="value">
                        {formatBankNumber(account.agency, account.agency_digit)}
                     </span>
                  </DataItem>
                  <DataItem>
                     <span className="label">Conta</span>
                     <span className="value">
                        {formatBankNumber(
                           account.account_number,
                           account.account_digit,
                        )}
                        {account.account_type
                           ? ` · ${
                                ACCOUNT_TYPE_LABELS[account.account_type] ??
                                account.account_type
                             }`
                           : ''}
                     </span>
                  </DataItem>

                  <DataItem>
                     <span className="label">Chave PIX</span>
                     <span className="value">{formatPixKey(account)}</span>
                  </DataItem>
                  <DataItem>
                     <span className="label">Cadastrada em</span>
                     <span className="value">
                        {formatDateTimeBr(account.created_at)}
                     </span>
                  </DataItem>
                  <DataItem>
                     <span className="label">Última atualização</span>
                     <span className="value">
                        {formatDateTimeBr(account.updated_at)}
                     </span>
                  </DataItem>
               </DataGrid>
            </SectionBody>
         </Section>

         {/* ─── Credenciais ───────────────────────────────────────────────── */}
         {canReadCredentials && (
            <CredentialsSection
               bankAccountId={account.id}
               accountStatus={account.status}
               credentials={credentials}
               onChanged={fetchAll}
            />
         )}

         <ConfirmDialog
            isOpen={statusDialogOpen}
            onClose={() => setStatusDialogOpen(false)}
            onConfirm={handleToggleStatus}
            title={isActive ? 'Desativar conta' : 'Reativar conta'}
            confirmLabel={isActive ? 'Desativar' : 'Reativar'}
            intent={isActive ? 'danger' : 'write'}
            isBusy={isSavingStatus}
         >
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55 }}>
               {isActive
                  ? 'A conta deixa de operar e não será possível cadastrar ou reativar credenciais nela. As cobranças já emitidas continuam válidas — nada é apagado, e você pode reativá-la depois.'
                  : 'A conta volta a operar. Se ela não tiver credencial ativa, ainda será preciso cadastrar (ou reativar) a chave da ASAAS para emitir cobranças.'}
            </p>
         </ConfirmDialog>
      </Container>
   );
}
